const PHASE_LABELS = {
  HT: 'Half time',
  BT: 'Break',
  P: 'Penalties',
  ET: 'Extra time',
  SUSP: 'Suspended',
  INT: 'Interrupted'
};

const LIVE_LISTEN_BUFFER_MS = 60 * 1000;
/** Keep AppSync open after FT so STANDINGS_PROCESSING / UPDATED can arrive. */
const LIVE_LISTEN_AFTER_FINISH_MS = 5 * 60 * 1000;

/**
 * @param {number | null | undefined} minute
 * @param {number | null | undefined} extraMinute
 * @returns {string | null}
 */
export function formatLiveMinuteDisplay(minute, extraMinute) {
  const m = Number(minute);
  if (!Number.isFinite(m) || m < 0) return null;

  const extra = Number(extraMinute);
  if (Number.isFinite(extra) && extra > 0) return `${m} + ${extra}`;
  return `${m}'`;
}

/**
 * @param {Record<string, unknown> | null | undefined} game
 */
function livePhaseOf(game) {
  return String(game?.livePhase ?? game?.lph ?? '').toUpperCase();
}

/**
 * @param {Record<string, unknown> | null | undefined} game
 */
function liveMinuteOf(game) {
  const minute = Number(game?.liveMinute ?? game?.lm);
  return Number.isFinite(minute) ? minute : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} game
 */
function liveExtraMinuteOf(game) {
  const extra = Number(game?.liveExtraMinute ?? game?.lx);
  return Number.isFinite(extra) && extra > 0 ? extra : 0;
}

/**
 * Format match clock from period wall-clock starts (FE ticker).
 * 1H: ≤45 → "N'"; >45 → "45 + (N-45)" (first stoppage is 45+1).
 * 2H: ≤90 → "N'"; >90 → "90 + (N-90)".
 *
 * @param {Record<string, unknown> | null | undefined} game
 * @param {number} [nowMs]
 * @returns {string | null}
 */
export function formatClockFromPeriodStarts(game, nowMs = Date.now()) {
  if (!game || game.status !== 'live') return null;

  const phase = livePhaseOf(game);
  if (phase === 'HT') return 'Half time';

  if (phase === '1H' || phase === '') {
    const start = Number(game.firstHalfStartedAt ?? game.fhsa);
    if (!Number.isFinite(start)) return null;
    const elapsed = Math.floor((nowMs - start) / 60_000);
    if (elapsed < 0) return "0'";
    if (elapsed <= 45) return `${elapsed}'`;
    return `45 + ${elapsed - 45}`;
  }

  if (phase === '2H') {
    const start = Number(game.secondHalfStartedAt ?? game.shsa);
    if (!Number.isFinite(start)) return null;
    const halfElapsed = Math.floor((nowMs - start) / 60_000);
    if (halfElapsed < 0) return "45'";
    const elapsed = 45 + halfElapsed;
    if (elapsed <= 90) return `${elapsed}'`;
    return `90 + ${elapsed - 90}`;
  }

  return null;
}

/**
 * Format clock by extrapolating a polled liveMinute from when it was observed.
 * AppSync does not push minute ticks — without this, badges freeze between polls.
 *
 * @param {Record<string, unknown> | null | undefined} game
 * @param {number} nowMs
 * @param {{ observedAtMs: number, liveMinute: number, liveExtraMinute?: number } | null | undefined} minuteAnchor
 * @returns {string | null}
 */
export function formatClockFromLiveMinuteAnchor(game, nowMs, minuteAnchor) {
  if (!game || game.status !== 'live' || !minuteAnchor) return null;
  const phase = livePhaseOf(game);
  if (phase === 'HT' || phase === 'BT' || phase === 'P') return null;

  const baseMinute = Number(minuteAnchor.liveMinute);
  if (!Number.isFinite(baseMinute)) return null;
  const baseExtra = Number(minuteAnchor.liveExtraMinute);
  const extra = Number.isFinite(baseExtra) && baseExtra > 0 ? baseExtra : 0;
  const observedAt = Number(minuteAnchor.observedAtMs);
  if (!Number.isFinite(observedAt)) return null;

  // Stoppage / regulation end: hold the official minute. AppSync does not push
  // minute ticks; inventing stoppage past lx (e.g. after a disconnect) shows 90+9
  // while the provider is still at 90+5.
  const holdOfficial =
    extra > 0 ||
    (phase === '2H' && baseMinute >= 90) ||
    ((phase === '1H' || phase === '') && baseMinute >= 45);
  if (holdOfficial) {
    return formatLiveMinuteDisplay(baseMinute, extra);
  }

  const baseTotal = baseMinute + extra;
  const advanced = Math.max(0, Math.floor((nowMs - observedAt) / 60_000));
  const total = baseTotal + advanced;

  if (phase === '2H') {
    if (total <= 90) return `${total}'`;
    return `90 + ${total - 90}`;
  }

  // 1H and other in-play phases (ET etc.): treat total as elapsed display basis
  if (phase === '1H' || phase === '' || phase === 'ET') {
    if (phase !== 'ET' && total <= 45) return `${total}'`;
    if (phase !== 'ET') return `45 + ${total - 45}`;
  }

  return formatLiveMinuteDisplay(total, 0);
}

/**
 * Whether the badge should tick every second (period starts or liveMinute fallback).
 * @param {Record<string, unknown> | null | undefined} game
 */
export function shouldTickLiveClock(game) {
  if (!game || game.status !== 'live') return false;
  const phase = livePhaseOf(game);
  if (phase === 'HT' || phase === 'BT' || phase === 'P') return false;
  if (phase === '2H') {
    if (Number.isFinite(Number(game.secondHalfStartedAt ?? game.shsa))) return true;
  } else if (Number.isFinite(Number(game.firstHalfStartedAt ?? game.fhsa))) {
    return true;
  }
  return liveMinuteOf(game) != null;
}

/**
 * Whether period wall-clock starts are available for the current phase.
 * @param {Record<string, unknown> | null | undefined} game
 */
export function hasPeriodStartForPhase(game) {
  if (!game || game.status !== 'live') return false;
  const phase = livePhaseOf(game);
  if (phase === '2H') {
    return Number.isFinite(Number(game.secondHalfStartedAt ?? game.shsa));
  }
  if (phase === 'HT' || phase === 'BT' || phase === 'P') return false;
  return Number.isFinite(Number(game.firstHalfStartedAt ?? game.fhsa));
}

/**
 * Human-readable live badge for match cards.
 * Prefers polled/websocket liveMinute (with optional FE tick anchor) so wall-clock
 * period starts cannot overrun the official match clock; holds stoppage at the
 * official minute/extra until the next update. Falls back to period starts when
 * no liveMinute has arrived yet.
 * @param {Record<string, unknown> | null | undefined} game
 * @param {number} [nowMs]
 * @param {{ observedAtMs: number, liveMinute: number, liveExtraMinute?: number } | null} [minuteAnchor]
 * @returns {string | null}
 */
export function formatLiveMatchClock(game, nowMs = Date.now(), minuteAnchor = null) {
  if (!game || game.status !== 'live') return null;

  const phase = livePhaseOf(game);

  if (phase === 'HT') return 'Half time';

  const minute = liveMinuteOf(game);
  const extraMinute = liveExtraMinuteOf(game);
  const clock = minute != null ? formatLiveMinuteDisplay(minute, extraMinute) : null;

  if (phase === 'BT') return clock ? `Break · ${clock}` : 'Break';
  if (phase === 'P') return clock ? `Penalties · ${clock}` : 'Penalties';

  const phaseLabel = PHASE_LABELS[phase];
  if (phaseLabel && phase !== '1H' && phase !== '2H') {
    return clock ? `${phaseLabel} · ${clock}` : phaseLabel;
  }

  // Official provider minute wins over period wall-clock (stoppage drift).
  if (minute != null) {
    const fromAnchor = formatClockFromLiveMinuteAnchor(game, nowMs, minuteAnchor);
    if (fromAnchor) return fromAnchor;
    return clock;
  }

  const fromPeriod = formatClockFromPeriodStarts(game, nowMs);
  if (fromPeriod) return fromPeriod;

  return 'Live';
}

/**
 * Connect to AppSync Events when **any** cached game is live, within 1 min of
 * kickoff, or within 5 min after full time. Overlapping slates are OR'd — one
 * FT must not drop the socket while another match still qualifies.
 * @param {Array<Record<string, unknown>> | null | undefined} games
 * @param {number} [nowMs]
 */
export function shouldListenForLiveGames(games, nowMs = Date.now()) {
  if (!Array.isArray(games) || games.length === 0) return false;
  return games.some((g) => {
    const status = String(g?.status || '').toLowerCase();
    if (status === 'live' || status === 'in_play' || status === 'active') return true;
    if (status === 'finished') {
      const terminalAt = Number(g.terminalAt);
      if (!Number.isFinite(terminalAt)) return false;
      return nowMs <= terminalAt + LIVE_LISTEN_AFTER_FINISH_MS;
    }
    if (status !== 'scheduled') return false;
    const kickoff = Number(g.gameDate);
    if (!Number.isFinite(kickoff)) return false;
    return kickoff - LIVE_LISTEN_BUFFER_MS <= nowMs && kickoff + 3 * 60 * 60 * 1000 >= nowMs;
  });
}

export { LIVE_LISTEN_BUFFER_MS, LIVE_LISTEN_AFTER_FINISH_MS };
